import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AlignJustify } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useParams } from "react-router-dom";
import {
  useCreateProjectGroupsScenarios,
  useUpdateProjectGroupsScenariosById,
} from "@/libs/react-query/hooks/useScenarioQuery";
import { showToast } from "@/utils/toastUtils";
import ApprovalScenarioDialog from "./ApprovalScenarioDialog";
import { Scenario } from "@/types/scenario";
import CreateOrEditScenarioDialog, {
  ScenarioSubmitPayload,
} from "./CreateOrEditScenarioDialog";
import { useState } from "react";
import {
  convertScenarioToGroupingTable,
  formatScenarioRequestData,
  getNextScenarioTitle,
} from "../utils";
import DeleteScenarioDialog from "./DeleteScenarioDialog";
import { useIsFinancialManager } from "@/hooks/useIsFinancialManager";
import CommonImg from "@/components/Common/Img";
import CopyPng from "@/assets/images/copy.png";

type Props = {
  scenario: Scenario;
  scenarios: Scenario[];
};

export default function ScenarioHeaderActions({ scenario, scenarios }: Props) {
  const { group_id = "" } = useParams();
  const [dialogOpen, setDialogOpen] = useState(false);

  const isFinancialManager = useIsFinancialManager();

  const { mutateAsync: editScenario } = useUpdateProjectGroupsScenariosById(
    group_id,
    scenario.scenario_id
  );
  const { mutate: createScenario } = useCreateProjectGroupsScenarios(group_id);

  const handleEdit = async (data: ScenarioSubmitPayload) => {
    if (!data?.divisions) {
      return null;
    }
    const { latestSheetId, title, divisions } = data;
    const formattedData = formatScenarioRequestData(divisions);

    try {
      await editScenario(
        {
          project_id: scenario.project_id,
          project_type: scenario.project_type,
          title,
          ref_item_sheet_id: latestSheetId,
          data: formattedData,
        },
        {
          onSuccess: () => {
            setDialogOpen(false);
            showToast({
              message: "시나리오가 수정되었습니다.",
              type: "success",
            });
          },
        }
      );
    } catch (error: any) {
      showToast({
        message: "시나리오 수정에 실패했습니다.",
        type: "error",
        description: error?.response?.data?.display_message,
      });
    }
  };

  const handleCopyScenario = () => {
    const { ref_item_sheet_id, data } = scenario;

    const title = getNextScenarioTitle(scenarios);

    createScenario(
      {
        project_id: scenario.project_id,
        project_type: scenario.project_type,
        ref_item_sheet_id,
        title,
        data,
      },
      {
        onSuccess: () => {
          showToast({
            message: "시나리오가 복제되었습니다.",
            type: "success",
          });
        },
        onError: (error: any) => {
          showToast({
            message: "시나리오 복제에 실패했습니다.",
            type: "error",
            description: error?.response?.data?.display_message,
          });
        },
      }
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
          <AlignJustify className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-fit text-[8px] font-semibold bg-white">
        <div className="flex flex-col">
          <ApprovalScenarioDialog scenario={scenario} />
          {isFinancialManager && (
            <>
              <button
                className="text-gray-A px-5 py-2 flex gap-1 hover:bg-gray-E"
                onClick={handleCopyScenario}
              >
                <CommonImg src={CopyPng} alt="C" className="w-3 h-3" />
                시나리오 복제
              </button>
              <CreateOrEditScenarioDialog
                mode="edit"
                open={dialogOpen}
                setOpen={setDialogOpen}
                defaults={{
                  sheetId: scenario.ref_item_sheet_id,
                  title: scenario.title,
                  projectId: scenario.project_id,
                  scenarioData: convertScenarioToGroupingTable(scenario.data),
                }}
                onSubmit={handleEdit}
              />
              <DeleteScenarioDialog scenario={scenario} />
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
